/* src/components/Feed.js - v7.24 - Fix Remembered Privacy Being Clobbered
   CHANGELOG (v7.23 -> v7.24): Real bug hit live immediately after v7.23
   shipped - reported as "privacy setting reverts to Public". Root cause:
   submitPost()'s own post-submit reset block had an existing, unrelated
   `setPostVisibility('public')` (predates the remember feature) that ran
   after EVERY post regardless of the remember_post_visibility pref - so
   picking Friends, posting, worked for that one post, but the selector
   itself snapped back to Public a moment later for the next post no
   matter what the setting said. Now only resets to Public when the user
   has explicitly opted out (remember_post_visibility === false);
   otherwise postVisibility is left as-is, since it's already sitting on
   whatever was just picked.
   CHANGELOG (v7.22 -> v7.23): Per explicit request, the composer's
   Public/Friends/Only Me selector now defaults to the user's last-used
   choice instead of always resetting to Public, gated on a new
   remember_post_visibility pref (Profile.js's "Remember my post privacy
   setting" toggle, default true - see that file's changelog for the
   copy). The actual "remember what they picked" write happens server-
   side (routes/posts.py's handle_posts(), not here) so it's correct
   regardless of which client posts - this component only reads the
   result (user.default_post_visibility) to initialize/re-sync its own
   local postVisibility state.
   CHANGELOG (v7.21 -> v7.22): Real bug hit live (2026-08-27): a 2.2MB
   video upload failed with a bare "Upload failed." - turned out nginx on
   the cloud VM (where this app is actually served from, not Nexus - see
   [[project-nsn]] memory) defaults to a 1MB body-size limit and returned
   its own HTML 413 page before Flask ever saw the request, which broke
   res.json() entirely with no useful detail surfaced. Fixed at the nginx
   layer (client_max_body_size 260M added to the social vhost), plus
   defense in depth here: handleFileSelect/handleVideoSelect now check
   file.size client-side against the same 50MB/250MB caps the backend
   enforces before even attempting an upload, and describeUploadError()
   falls back to a size-aware message when a response isn't valid JSON
   (any similar proxy/gateway-level rejection in the future degrades to a
   useful message instead of a dead end). Photo/Video button also has a
   title tooltip stating both limits upfront, per explicit request to
   surface requirements rather than only failure reasons.
   CHANGELOG (v7.2 -> v7.21): Video Post Attachments
   CHANGELOG (v7.2 -> v7.21): Composer can now attach video, not just
   photos/PDFs. Picking a video (accept="image/*,video/*,application/pdf"
   below now includes video/*) goes through a separate async flow from
   the existing synchronous image/PDF upload: POST /api/upload/video
   returns a job_id immediately, then a 3s poll against
   GET /api/upload/video/<job_id> (new videoState state + pollVideoJob())
   until status flips to 'ready' or 'failed' - the transcode itself can
   take real time, so this can't reuse handleFileSelect's "upload then
   immediately have a URL" shape. POST is disabled while videoState is
   'uploading'/'processing', same as it already was for isUploading, so a
   post can't be submitted with a video that isn't playable yet. Selecting
   a video clears any in-progress image selection and vice versa - a post
   carries one attachment, not both, even though the composer doesn't
   otherwise prevent picking both in sequence. See Post.js v4.25 for the
   feed-side <video> rendering this feeds into.
   CHANGELOG (v7.19 -> v7.2): Added hasMore/onLoadMore/
   loadingMore props - the main feed is now paginated server-side
   (App.js's fetchPosts/loadMorePosts, routes/posts.py v2.13's extended
   /api/posts/archive), so this needs a way to actually request the next
   page. Deliberately NOT a "Load Older" button - v7.13 removed that
   exact pattern from archive mode per request, so this uses a silent
   IntersectionObserver sentinel at the bottom of the list instead (auto-
   loads the next page as it scrolls into view, no visible button). Only
   active when hasMore is true and not already loadingMore; skipped
   entirely in archiveMode (unchanged - Timeline still shows a single
   fixed page, per that same v7.13 request).
   CHANGELOG (v7.18 -> v7.19): Accepts and forwards the new onVoteComment
   prop straight through to Post.js, same as onAddComment already is.
   CHANGELOG (v7.17 -> v7.18): Post.js's new comment GIF picker needs the
   Giphy API key to search - rather than have every rendered Post
   independently fetch /api/config/public (N redundant fetches per feed
   page), this component already holds giphyKey in state for its own
   composer, so it's simplest to just pass it straight down as a prop.
   CHANGELOG (v7.16 -> v7.17): The main feed render never excluded
   visibility:'group' posts, so any group member would see their group's
   posts duplicated into the general Newsfeed too, alongside the group's
   own page. Now filtered out via `groupContext || p.visibility !== 'group'`
   right at the render map - only applies when NOT already viewing inside
   a group (groupContext falsy), since GroupDetails.js's own posts prop is
   already pre-filtered to one group when groupContext is set. Same bug
   fixed in mobile's NewsfeedScreen.js v1.7, same release.
   CHANGELOG (v7.15 -> v7.16): Per explicit request, the top color-strip
   banner (getSentimentBadge, added v7.14/v7.15) is gone entirely - it
   contrasted too harshly against the card. Sentiment is now shown as a
   right-side accent border on the post card itself instead, mirroring the
   existing left border (post.color) - see Post.js v4.15, which is where
   that logic now lives. getGlowStyle (the box-shadow glow) is unrelated
   and untouched.
   CHANGELOG (v7.14 -> v7.15): v7.14's height:10px/marginBottom:-8px only
   left 2px of the strip actually visible above the card (height minus the
   overlap) - effectively invisible in practice, per report with a
   screenshot showing it not showing up at all. Bumped to height:20px/
   marginBottom:-10px (10px visible) - same plain color band, just
   actually visible now. Same fix applied to mobile's PostCard.js v1.8.
   CHANGELOG (v7.13 -> v7.14): Per explicit request to make this "more
   subtle" - getSentimentBadge() no longer renders the emoji + "Positive
   Vibes"/"Tense" text at all. Now just a thin solid-color strip (green/
   red), no dark blurred panel, no bold uppercase text - same change made
   to mobile's PostCard.js. Still uses the same negative marginBottom
   overlap to tuck behind the card's rounded top corner (see v7.11/v7.12
   changelogs below for why that specific overlap amount was chosen -
   this version uses a smaller overlap since there's no text height to
   protect anymore).
   CHANGELOG (v7.12 -> v7.13): Per request, removed the "Load Older"
   pagination button from archive mode, along with everything it
   depended on: archiveHasMore state, the has_more tracking in
   fetchArchivePosts (simplified back to a plain fetch-and-set, no more
   append/has_more branching), and handleLoadOlderArchive itself.
   Jumping to a month still shows that month's posts; there's just no way
   to page further back from there anymore.
   CHANGELOG (v7.11 -> v7.12): v7.11's -15px overlap was larger than the
   badge's own effective height (thin 5px padding + ~14px text), so the
   overlap ate into the visible "Positive Vibes"/"Tense" text itself, not
   just empty space below it. Fixed by giving the badge more height via
   padding (10px top, 18px bottom) so there's enough room above the
   overlap zone for the text to render fully - the bottom padding (18px)
   now exceeds the overlap (15px), so the overlap only ever consumes
   empty padding, never the text.
   CHANGELOG (v7.10 -> v7.11): Added marginBottom: '-15px' to the
   sentiment badge, tucking it behind the post card's own rounded top
   corner instead of sitting flush above it. Post.js's card uses the
   shared .card class for its rounding (not an inline value I could read
   directly), but the overlap is generous enough to reliably mask a
   typical corner radius regardless of its exact pixel value - any excess
   overlap is simply hidden behind the post's own solid background where
   it isn't curved. Fills the small gap where the wallpaper was visible
   through the post's rounded corner, without changing the post's own
   curve at all.
   CHANGELOG (v7.9 -> v7.10): Per request, the Timeline panel is now a
   centered modal overlay (dark backdrop + blur, click-outside or ✕ to
   close) instead of an inline card pushed into the feed's normal flow -
   matches the exact same overlay styling already used for the Create
   Post modal, for visual consistency. No behavior changes otherwise.
   CHANGELOG (v7.8 -> v7.9): The Timeline/archive feature (year/month jump
   list, "Load Older", "Back to Today") was completely missing - a second
   instance of the same v7.5 full-file-rewrite mistake that reverted the
   sentiment badge fix (v7.8). App.js v13.28 was already correctly sending
   showTimeline/onCloseTimeline props to Feed - this file just had no code
   left to read them, so clicking the calendar icon correctly toggled
   App.js's state but nothing here responded to it (no fetch was ever
   attempted, which is why there was no network request AND no backend
   log output - it wasn't a backend bug at all).
   Restored: showTimeline/onCloseTimeline props, an archive-index fetch
   effect, fetchArchivePosts/handleJumpToDate/handleLoadOlderArchive/
   handleExitArchive handlers, a Timeline panel (year/month picker) and an
   archive-mode banner, both rendered inline in this component (there is
   no separate web Timeline.js file - that name refers only to the mobile
   app's own React Native component, a different codebase entirely).
   Post list now shows archivePosts instead of the live posts prop while
   archiveMode is active.
   CHANGELOG (v7.7 -> v7.8): getSentimentBadge() had reverted back to its
   ORIGINAL near-invisible styling (rgba(...,0.15) - 15% opacity, blends
   into the wallpaper). This was a real mistake, not new breakage: v7.5
   rebuilt this file from scratch for the group-posting feature, using the
   original session-start upload as the reference copy - which predated
   the earlier fix to this exact function. A full-file rewrite doesn't
   merge with untracked changes, it replaces everything, so that earlier
   fix was silently discarded. Restored here: opaque-ish background (0.85)
   with blur and a colored bottom border, font bumped back to 12px.
   CHANGELOG (v7.6 -> v7.7): v7.6 submitted one post per selected group via
   a plain forEach, firing all requests essentially simultaneously.
   Confirmed via testing: deleting one still deleted all of them - the
   requests were arriving close enough together that the backend's id
   generation (int(time.time()*1000), millisecond resolution) could
   produce the SAME id for what were supposed to be independent posts,
   and/or posts.json's unprotected read-modify-write could let one
   request's insert get silently overwritten by another's save. Fixed by
   making submitPost async and awaiting each group's post creation in
   sequence (a for...of loop) instead of firing them all at once - each
   request now fully completes (backend has saved it) before the next
   one starts, guaranteeing distinct timestamps/ids.
   CHANGELOG (v7.5 -> v7.6): Per explicit request, posting to multiple
   groups now submits one independent post per selected group
   (group_slugs:[thatOneGroup] each, own id) instead of one shared post
   with group_slugs:[...all selected]. Deleting the post from one group's
   feed now only deletes that group's own record, leaving the others
   intact - not possible with the shared-post approach, since a single
   DELETE /api/posts/<id> would have removed it everywhere at once.
   CHANGELOG (v7.1 -> v7.5):
   - Added groupContext and userGroups props. groupContext is the slug of
     the group this Feed instance belongs to (set only when GroupDetails.js
     renders it - undefined/null for the main Newsfeed, so that path is
     completely unchanged). userGroups is the list of groups the current
     user is a member of (name + slug), used to build the "Post to:"
     checklist.
   - When groupContext is set, the visibility <select> (Public/Friends/
     Only Me) is replaced with a checklist of the user's groups (their
     own current group pre-checked), matching the requirement that group
     posts have no Public/Friends/Only Me option at all - it's a
     genuinely separate, mutually exclusive privacy mode. submitPost()
     sends visibility:'group' and group_slugs:[...checked slugs] instead
     of visibility:postVisibility in this case.
   - POST is disabled if in group context with zero groups checked,
     mirroring the backend's server-side validation (posts.py v2.3) that
     rejects a group post with an empty group_slugs list.
   - The main Newsfeed's own composer (groupContext absent) is completely
     unchanged - same Public/Friends/Only Me dropdown as before. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Post from './Post';
import AdPost from './AdPost';
import VALID_LOCATIONS from '../locations.json'; // IMPORTED FROM JSON

// Matches routes/posts.py's MAX_IMAGE_DOC_UPLOAD_SIZE / MAX_VIDEO_UPLOAD_SIZE
// on Nexus - checked client-side first so an oversized file gets an
// immediate, specific message instead of a round trip that might come
// back as a non-JSON error page (nginx's own 413, a gateway timeout,
// etc.) with no useful detail to show. The server-side checks remain the
// real enforcement; this is just a faster, friendlier first line.
const MAX_IMAGE_DOC_UPLOAD_SIZE = 50 * 1024 * 1024;
const MAX_VIDEO_UPLOAD_SIZE = 250 * 1024 * 1024;
const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

const AI_TONES = [
    { label: '✨ Standard', value: 'standard' },
    { label: '👔 Professional', value: 'professional' },
    { label: '🏴‍☠️ Pirate', value: 'pirate' },
    { label: '🤩 Enthusiastic', value: 'enthusiastic' },
    { label: '😏 Sarcastic', value: 'sarcastic' }
];

const COMMON_EMOJIS = [
    '😀','😂','😍','😎','🤔','😭','😡','👍','👎','🔥',
    '❤️','💔','✨','🎉','🚀','💀','🤡','💩','👻','👽',
    '🤖','🐱','🐶','🍕','🍺','☕','💻','📱','🔋','💡'
];

const FALLBACK_GIFS = [
    { id: 'f1', images: { fixed_height: { url: "https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif" }, original: { url: "https://media.giphy.com/media/l0HlHJGHe3yAMhdQY/giphy.gif" } } },
    { id: 'f2', images: { fixed_height: { url: "https://media.giphy.com/media/3o7TKr3nzbh5WgCFxe/giphy.gif" }, original: { url: "https://media.giphy.com/media/3o7TKr3nzbh5WgCFxe/giphy.gif" } } },
    { id: 'f3', images: { fixed_height: { url: "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif" }, original: { url: "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif" } } }
];

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function Feed({
    user, posts, onAddPost, onDeletePost, onLikePost,
    onAddComment, onVoteComment, onDeleteComment, onEditComment, onEdit, onNavigateProfile,
    onTriggerLogin,
    showInput = true,
    groupContext = null,
    userGroups = [],
    showTimeline = false,
    onCloseTimeline = () => {},
    hasMore = false,
    onLoadMore = () => {},
    loadingMore = false,
    initialText = null,
    onInitialTextConsumed = () => {}
}) {
  const loadMoreSentinelRef = useRef(null);
  
  const [inputText, setInputText] = useState('');

  // Post-signup intro-post prompt (App.js sets this once, right after a
  // fresh registration's first login) - pre-fill the composer and focus
  // it instead of dropping the new user into an empty feed. Consumed
  // (cleared in the parent) immediately so it can't reappear on a later
  // render.
  useEffect(() => {
      if (initialText) {
          setInputText(initialText);
          onInitialTextConsumed();
          setTimeout(() => textareaRef.current?.focus(), 0);
      }
  }, [initialText, onInitialTextConsumed]);
  const [originalText, setOriginalText] = useState(''); 
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [videoState, setVideoState] = useState(null); // null | { status: 'uploading'|'processing'|'ready'|'failed', url, thumbnail, error }
  
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isUploading, setIsUploading] = useState(false); 
  const [activeMenu, setActiveMenu] = useState(null); 
  const [showPostModal, setShowPostModal] = useState(false);
  const [giphyKey, setGiphyKey] = useState(null);

  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState([]);
  const [isGifLoading, setIsGifLoading] = useState(false);

  const [feeling, setFeeling] = useState(''); 
  const [location, setLocation] = useState(''); 
  const [geoLocating, setGeoLocating] = useState(false);
  const [postVisibility, setPostVisibility] = useState(() => (user?.remember_post_visibility !== false && user?.default_post_visibility) || 'public');

  // Keeps the composer's visibility selector in sync with the user's
  // remembered default (routes/posts.py auto-updates default_post_visibility
  // server-side whenever they post with a different choice, if the
  // "remember" pref is on) - covers login happening after this component
  // already mounted (guest -> logged-in without a full remount), where the
  // useState initializer above only ran once against a null user.
  useEffect(() => {
      if (user?.remember_post_visibility !== false && user?.default_post_visibility) {
          setPostVisibility(user.default_post_visibility);
      }
  }, [user?.remember_post_visibility, user?.default_post_visibility]);
  const [selectedGroups, setSelectedGroups] = useState(groupContext ? [groupContext] : []);

  const [archiveIndex, setArchiveIndex] = useState({});
  const [archiveIndexLoading, setArchiveIndexLoading] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);
  const [archiveMode, setArchiveMode] = useState(null); // { year, month } | null

  useEffect(() => {
      if (archiveMode || !hasMore) return;
      const el = loadMoreSentinelRef.current;
      if (!el) return;
      const observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && !loadingMore) onLoadMore();
      }, { rootMargin: '400px' });
      observer.observe(el);
      return () => observer.disconnect();
  }, [archiveMode, hasMore, loadingMore, onLoadMore]);
  const [archivePosts, setArchivePosts] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoPollRef = useRef(null);

  const feelingsList = ['Happy 😊', 'Excited 🤩', 'Tired 😴', 'Coding 💻', 'Blessed 😇', 'Annoyed 😤'];

  useEffect(() => {
      fetch('/api/config/public')
        .then(res => res.json())
        .then(data => {
            if(data.giphy_api_key) setGiphyKey(data.giphy_api_key);
        })
        .catch(err => console.error("Config fetch error", err));
  }, []);

  // --- TIMELINE / ARCHIVE ---
  useEffect(() => {
      if (showTimeline && Object.keys(archiveIndex).length === 0) {
          setArchiveIndexLoading(true);
          fetch(`/api/posts/archive-index?current_user=${user?.name || ''}`)
              .then(res => res.json())
              .then(data => setArchiveIndex(data))
              .catch(err => console.error('Archive index error', err))
              .finally(() => setArchiveIndexLoading(false));
      }
  }, [showTimeline, user, archiveIndex]);

  const fetchArchivePosts = async (beforeTimestamp) => {
      setArchiveLoading(true);
      try {
          const params = new URLSearchParams({ limit: 30, current_user: user?.name || '' });
          if (beforeTimestamp) params.set('before', beforeTimestamp);
          const res = await fetch(`/api/posts/archive?${params}`);
          const data = await res.json();
          setArchivePosts(data.posts);
      } catch (e) { console.error('Archive fetch error', e); }
      setArchiveLoading(false);
  };

  const handleJumpToDate = (year, month) => {
      // Fetch everything strictly before the START of the NEXT month, so
      // the page includes the whole selected month, since posts are
      // stored/returned newest-first.
      const nextMonth = month === 12 ? 1 : parseInt(month) + 1;
      const nextYear = month === 12 ? parseInt(year) + 1 : parseInt(year);
      const boundary = new Date(nextYear, nextMonth - 1, 1).getTime();
      setArchiveMode({ year, month });
      fetchArchivePosts(boundary);
      setExpandedYear(null);
  };

  const handleExitArchive = () => {
      setArchiveMode(null);
      setArchivePosts([]);
  };

  useEffect(() => {
      const handleEsc = (e) => {
          if (e.key === 'Escape') {
              if (activeMenu) setActiveMenu(null);
              else if (showPostModal) setShowPostModal(false);
          }
      };
      if (showPostModal) window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
  }, [showPostModal, activeMenu]);

  const toggleMenu = (menuName) => {
      setActiveMenu(activeMenu === menuName ? null : menuName);
  };

  const closeMenus = () => setActiveMenu(null);

  const searchGifs = useCallback(async (term) => {
      setIsGifLoading(true);
      try {
          if (!giphyKey) {
              setGifs(FALLBACK_GIFS);
              setIsGifLoading(false);
              return;
          }
          const query = term || "trending";
          const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${giphyKey}&q=${query}&limit=20&rating=g`);
          const data = await res.json();
          if (data.meta && data.meta.status === 200 && data.data.length > 0) setGifs(data.data);
          else setGifs(FALLBACK_GIFS);
      } catch (e) { setGifs(FALLBACK_GIFS); }
      setIsGifLoading(false);
  }, [giphyKey]);

  const handleGifSelect = (gifUrl) => {
      setPreviewUrl(gifUrl);
      setFileType('image');
      setActiveMenu(null); 
  };

  useEffect(() => {
      if (activeMenu === 'gif') searchGifs("trending");
  }, [activeMenu, searchGifs]);

  const handleMagicRewrite = async (selectedTone) => {
      if (!originalText.trim()) return; 
      setIsAiThinking(true);
      setActiveMenu(null);
      try {
          const res = await fetch('/api/ai/rewrite', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: originalText, style: selectedTone.value })
          });
          const data = await res.json();
          if (data.text) setInputText(data.text);
      } catch (e) { console.error("AI Rewrite failed", e); }
      setIsAiThinking(false);
  };

  // --- QUICK POST: a few multiple-choice questions -> AI drafts a post
  // from the picks, landing back in the normal composer for review/edit
  // before Post is actually clicked. Tap-to-answer (with an "Other" for
  // a custom answer) instead of free typing, since typing answers to
  // questions isn't meaningfully less effort than just writing the post.
  const QUICK_POST_QUESTION_POOL = [
      { question: "How's your day going?", options: ["Pretty good!", "Just okay", "Kind of rough", "Amazing!"] },
      { question: "What's something you're looking forward to?", options: ["The weekend", "A trip", "Seeing friends", "Nothing in particular"] },
      { question: "Anything exciting happen recently?", options: ["Yes, big news!", "A small win", "Not really", "Still waiting to find out"] },
      { question: "What's on your mind right now?", options: ["Work/school stuff", "Random thoughts", "Something I watched/read", "Nothing much"] },
      { question: "Any plans for today or this week?", options: ["Relaxing", "Staying busy", "Something fun planned", "No plans yet"] },
      { question: "What made you smile today?", options: ["A friend/family member", "Something funny online", "Good weather", "Nothing yet, still hoping"] },
  ];
  const QUICK_POST_OTHER = '__OTHER__';
  const [showQuickPost, setShowQuickPost] = useState(false);
  const [quickPostQuestions, setQuickPostQuestions] = useState([]);
  const [quickPostSelections, setQuickPostSelections] = useState({}); // question -> chosen option text, or QUICK_POST_OTHER
  const [quickPostOtherText, setQuickPostOtherText] = useState({}); // question -> custom text when Other is chosen
  const [quickPostGenerating, setQuickPostGenerating] = useState(false);

  const openQuickPost = () => {
      const shuffled = [...QUICK_POST_QUESTION_POOL].sort(() => Math.random() - 0.5);
      setQuickPostQuestions(shuffled.slice(0, 3));
      setQuickPostSelections({});
      setQuickPostOtherText({});
      setShowQuickPost(true);
      setActiveMenu(null);
  };

  const selectQuickPostOption = (question, option) => {
      setQuickPostSelections(prev => ({ ...prev, [question]: option }));
  };

  const handleQuickPostGenerate = async () => {
      const answers = quickPostQuestions
          .map(q => {
              const picked = quickPostSelections[q.question];
              const answer = picked === QUICK_POST_OTHER ? (quickPostOtherText[q.question] || '').trim() : (picked || '');
              return { question: q.question, answer };
          })
          .filter(a => a.answer);
      if (answers.length === 0) return;
      setQuickPostGenerating(true);
      try {
          const res = await fetch('/api/ai/compose-from-answers', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ answers })
          });
          const data = await res.json();
          if (data.text) {
              setInputText(data.text);
              setOriginalText(data.text);
              setShowQuickPost(false);
              setTimeout(() => textareaRef.current?.focus(), 0);
          }
      } catch (e) { console.error("Quick Post generation failed", e); }
      setQuickPostGenerating(false);
  };

  const handleInput = (e) => {
      const val = e.target.value;
      setInputText(val);
      setOriginalText(val); 
  };

  const addEmoji = (emoji) => {
      setInputText(prev => prev + emoji);
      setOriginalText(prev => prev + emoji);
  };

  const useMyLocation = () => {
      if (!navigator.geolocation) return;
      setGeoLocating(true);
      navigator.geolocation.getCurrentPosition(
          async (pos) => {
              try {
                  const { latitude, longitude } = pos.coords;
                  const res = await fetch(`/api/location/reverse-geocode?lat=${latitude}&lon=${longitude}`);
                  const data = await res.json();
                  if (data.location) { setLocation(data.location); setActiveMenu(null); }
              } catch (e) { console.error("Reverse geocode failed", e); }
              setGeoLocating(false);
          },
          () => setGeoLocating(false), // permission denied or unavailable - fail quietly, manual search still works
          { timeout: 10000 }
      );
  };

  const toggleGroupSelection = (slug) => {
      setSelectedGroups(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };

  const submitPost = async () => {
    if (!user) { onTriggerLogin(); return; } // GUEST GUARD
    if (groupContext && selectedGroups.length === 0) return; // must pick at least one group
    const hasReadyVideo = videoState && videoState.status === 'ready';
    if (inputText.trim() !== '' || previewUrl || hasReadyVideo) {
      const defaultTheme = "rgba(43, 43, 54, 0.60)";
      const postColor = (user.theme_color && user.theme_color !== defaultTheme) ? user.theme_color : user.avatarColor;

      const basePost = {
        username: user.name,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        message: inputText, image: previewUrl, fileType: fileType,
        video: hasReadyVideo ? videoState.url : undefined,
        video_thumbnail: hasReadyVideo ? videoState.thumbnail : undefined,
        feeling: feeling, location: location, color: postColor, userImage: user.profile_pic
      };

      if (groupContext) {
          // Each selected group gets its own independent post record
          // (its own id, its own group_slugs:[thatOneGroup]) rather than
          // one shared post with multiple group_slugs - so removing the
          // post from one group's feed doesn't remove it from the
          // others. Submitted sequentially (awaited one at a time), NOT
          // fired all at once - near-simultaneous requests risk the
          // backend's int(time.time()*1000) id generation colliding
          // (same millisecond -> same id), which would make "two"
          // independent posts actually share one id under the hood,
          // silently reversing the whole point of this fix (deleting one
          // would delete both again). Awaiting each call in turn gives
          // every request a distinct timestamp.
          for (const slug of selectedGroups) {
              await onAddPost({ ...basePost, visibility: 'group', group_slugs: [slug] });
          }
      } else {
          await onAddPost({ ...basePost, visibility: postVisibility });
      }

      setInputText(''); setOriginalText(''); setPreviewUrl(null); setFileType(null);
      setVideoState(null);
      setFeeling(''); setLocation('');
      // Only snap back to Public when the user has opted OUT of
      // remembering (explicit false) - this used to unconditionally
      // reset to 'public' after every post, which silently fought the
      // whole remembered-visibility feature: postVisibility is already
      // sitting on whatever they just picked, so with remembering on
      // the right move is to just leave it alone, not overwrite it.
      if (user?.remember_post_visibility === false) {
          setPostVisibility('public');
      }
      setSelectedGroups(groupContext ? [groupContext] : []);
      setShowPostModal(false); 
      setActiveMenu(null);
    }
  };

  const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submitPost();
      }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    if (file.type.startsWith('video/')) {
        setPreviewUrl(null); setFileType(null); // a post carries one attachment, not both
        handleVideoSelect(file);
        return;
    }
    handleRemoveVideo(); // clear any in-progress video selection
    if (file.size > MAX_IMAGE_DOC_UPLOAD_SIZE) {
        alert(`That file is ${formatMB(file.size)}MB - photos and documents are limited to 50MB.`);
        return;
    }
    setFileType(file.type === 'application/pdf' ? 'pdf' : 'image');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('username', user.name);
    setIsUploading(true);
    try {
        const res = await fetch('/api/upload', { method: 'POST', body: formData });
        if (res.ok) {
            const data = await res.json();
            setPreviewUrl(data.url);
        } else {
            alert(await describeUploadError(res, MAX_IMAGE_DOC_UPLOAD_SIZE, 'Photos and documents'));
        }
    } catch (err) { console.error("Upload error:", err); alert("Upload failed - check your connection and try again."); }
    finally { setIsUploading(false); }
  };

  // Best-effort error text for a failed upload response: the server
  // normally sends JSON ({"error": "..."}), but a request rejected before
  // it ever reaches Flask (nginx's own body-size limit, a proxy timeout,
  // Cloudflare's own error page) comes back as HTML instead - res.json()
  // throws on that, which used to collapse into a bare "Upload failed."
  // with no indication of why. Falls back to a size-aware guess (a 413
  // almost always means the file was too big) rather than a dead end.
  const describeUploadError = async (res, maxSize, label) => {
      try {
          const data = await res.json();
          if (data && data.error) return data.error;
      } catch (e) { /* not JSON - fall through to a status-based guess */ }
      if (res.status === 413) return `${label} are limited to ${formatMB(maxSize)}MB. Please choose a smaller file.`;
      return `Upload failed (error ${res.status}). Please try again.`;
  };

  const handleVideoSelect = async (file) => {
      if (file.size > MAX_VIDEO_UPLOAD_SIZE) {
          setVideoState({ status: 'failed', error: `That video is ${formatMB(file.size)}MB - videos are limited to ${formatMB(MAX_VIDEO_UPLOAD_SIZE)}MB.` });
          return;
      }
      setVideoState({ status: 'uploading' });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', user.name);
      try {
          const res = await fetch('/api/upload/video', { method: 'POST', body: formData });
          if (!res.ok) {
              setVideoState({ status: 'failed', error: await describeUploadError(res, MAX_VIDEO_UPLOAD_SIZE, 'Videos') });
              return;
          }
          const data = await res.json();
          setVideoState({ status: 'processing' });
          pollVideoJob(data.job_id);
      } catch (err) {
          console.error("Video upload error:", err);
          setVideoState({ status: 'failed', error: 'Upload failed - check your connection and try again.' });
      }
  };

  const pollVideoJob = (jobId) => {
      let attempts = 0;
      videoPollRef.current = setInterval(async () => {
          attempts += 1;
          if (attempts > 200) { // ~10 min ceiling, matches the backend's own transcode timeout
              clearInterval(videoPollRef.current);
              setVideoState({ status: 'failed', error: 'Video processing timed out.' });
              return;
          }
          try {
              const res = await fetch(`/api/upload/video/${jobId}`);
              const data = await res.json();
              if (data.status === 'ready') {
                  clearInterval(videoPollRef.current);
                  setVideoState({ status: 'ready', url: data.video_url, thumbnail: data.thumbnail_url });
              } else if (data.status === 'failed') {
                  clearInterval(videoPollRef.current);
                  setVideoState({ status: 'failed', error: data.error || 'Video processing failed.' });
              }
          } catch (err) { console.error("Video status poll error:", err); }
      }, 3000);
  };

  const handleRemoveVideo = () => {
      if (videoPollRef.current) { clearInterval(videoPollRef.current); videoPollRef.current = null; }
      setVideoState(null);
  };

  useEffect(() => {
      return () => { if (videoPollRef.current) clearInterval(videoPollRef.current); };
  }, []);

  const triggerFileUpload = () => {
      if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleShare = async (post, type) => {
      if (type === 'link') {
          const link = `${window.location.origin}/post/${post.id}`;
          try { await navigator.clipboard.writeText(link); alert("Link copied!"); } catch (err) {}
      } else if (type === 'repost') {
          if (!user) { onTriggerLogin(); return; }
          onAddPost({
            username: user.name, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            message: `🔄 Reposted from @${post.username}: "${post.message}"`,
            image: post.image, color: user.avatarColor, userImage: user.profile_pic,
            visibility: 'public'
          });
      }
  };

// --- FIXED GLOW (Increased Opacity & Direct Check) ---
  const getGlowStyle = (sentiment) => {
      if (!sentiment) return {};
      if (sentiment === 'POSITIVE') return { boxShadow: '0 0 25px rgba(46, 213, 115, 0.6)', border: '1px solid rgba(46, 213, 115, 0.6)' };
      if (sentiment === 'NEGATIVE') return { boxShadow: '0 0 25px rgba(255, 71, 87, 0.6)', border: '1px solid rgba(255, 71, 87, 0.6)' };
      return {};
  };

  const renderEditor = () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', position: 'relative' }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
            {user.profile_pic ? <img src={user.profile_pic} style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover' }} alt="Avatar" /> : <div style={{ background: user.avatarColor, width: '45px', height: '45px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>{user.name ? user.name[0].toUpperCase() : '?'}</div>}
            
            <div style={{ position: 'relative', flex: 1 }}>
                <textarea 
                    ref={textareaRef} 
                    className="post-input" 
                    style={{ width: '100%', padding: '18px 55px 60px 55px', borderRadius: '20px', resize: 'none', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', minHeight: '100px', fontSize: '16px', color: 'white' }} 
                    placeholder={`What's on your mind?`} 
                    value={inputText} 
                    onChange={handleInput} 
                    onKeyDown={handleKeyDown}
                    onClick={closeMenus}
                    autoFocus
                />
                {/* Corner-triangle shortcuts - deliberately more visible than a
                    small icon so people actually notice these two AI
                    helper tools exist. The label is a separate unclipped
                    sibling layered on top of the clip-path'd triangle
                    background (not a child of the clipped element) -
                    tried nesting the text inside the clipped div first
                    and the diagonal cut straight through the label at
                    normal font sizes/text lengths ("Quick Post" -> "Quic").
                    Decoupling them avoids that entirely regardless of
                    label length. */}
                <div
                    onClick={(e) => { e.stopPropagation(); openQuickPost(); }}
                    title="Quick Post - answer a few questions, AI drafts a post for you to review"
                    style={{ position: 'absolute', left: 0, bottom: 0, width: '150px', height: '60px', cursor: 'pointer' }}
                >
                    <div style={{ position: 'absolute', inset: 0, clipPath: 'polygon(0 100%, 0 45%, 100% 100%)', background: '#5a4a7a', borderRadius: '0 0 0 20px' }} />
                    <span style={{ position: 'absolute', left: '6px', bottom: '16px', fontSize: '11px', fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap', transform: 'rotate(12.4deg)', transformOrigin: 'left bottom' }}>🪄 Quick Post</span>
                </div>
                <div
                    onClick={(e) => { e.stopPropagation(); toggleMenu('ai'); }}
                    title="AI Rewrite"
                    style={{ position: 'absolute', right: 0, bottom: 0, width: '150px', height: '60px', cursor: 'pointer' }}
                >
                    <div style={{ position: 'absolute', inset: 0, clipPath: 'polygon(100% 100%, 100% 45%, 0 100%)', background: '#2d5f8a', borderRadius: '0 0 20px 0' }} />
                    <span style={{ position: 'absolute', right: '10px', bottom: '14px', fontSize: '11px', fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap', transform: 'rotate(-12.4deg)', transformOrigin: 'right bottom' }}>{isAiThinking ? '⏳ Rewriting...' : '✨ AI Rewrite'}</span>
                </div>
            </div>
          </div>

          {(previewUrl || isUploading) && (
              <div style={{ marginTop: '5px', position:'relative', minHeight:'50px', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.2)', borderRadius:'12px' }}>
                  {isUploading ? <span style={{ color: '#2ed573' }}>⏳ Uploading...</span> : (
                      <>
                        <img src={previewUrl} style={{ maxWidth: '100%', borderRadius: '12px', maxHeight:'300px' }} alt="Preview" />
                        <button onClick={() => setPreviewUrl(null)} style={{ position:'absolute', top:'10px', right:'10px', background:'rgba(0,0,0,0.7)', color:'white', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer' }}>✕</button>
                      </>
                  )}
              </div>
          )}

          {videoState && (
              <div style={{ marginTop: '5px', position:'relative', minHeight:'50px', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.2)', borderRadius:'12px', padding: videoState.status === 'ready' ? 0 : '20px', gap: '10px' }}>
                  {videoState.status === 'uploading' && <span style={{ color: '#2ed573' }}>⏳ Uploading video...</span>}
                  {videoState.status === 'processing' && <span style={{ color: '#2ed573' }}>🎬 Processing video... this can take a minute</span>}
                  {videoState.status === 'failed' && <span style={{ color: '#ff4757' }}>⚠️ {videoState.error}</span>}
                  {videoState.status === 'ready' && (
                      <>
                        <video src={videoState.url} poster={videoState.thumbnail} controls style={{ maxWidth: '100%', borderRadius: '12px', maxHeight:'300px' }} />
                        <button onClick={handleRemoveVideo} style={{ position:'absolute', top:'10px', right:'10px', background:'rgba(0,0,0,0.7)', color:'white', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer' }}>✕</button>
                      </>
                  )}
                  {videoState.status !== 'ready' && (
                      <button onClick={handleRemoveVideo} style={{ background:'transparent', color:'#ff4757', border:'1px solid #ff4757', borderRadius:'8px', padding:'4px 10px', cursor:'pointer' }}>Cancel</button>
                  )}
              </div>
          )}

          <div style={{ display: 'flex', gap: '15px', fontSize: '14px', flexWrap:'wrap', alignItems:'center', position: 'relative' }}>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*,video/*,application/pdf" onChange={handleFileSelect} />

                <span onClick={triggerFileUpload} title="Photos/documents up to 50MB. Videos up to 250MB." style={{ color: '#2ed573', cursor: 'pointer', display:'flex', alignItems:'center', gap:'5px', padding: '8px 12px', background: 'rgba(46, 213, 115, 0.1)', borderRadius: '12px' }}>
                    {isUploading ? '⏳ Uploading...' : '📁 Photo/Video'}
                </span>
                
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('gif'); }} style={{ color: '#a55eea', cursor: 'pointer', display:'flex', alignItems:'center', gap:'5px', padding: '8px 12px', background: 'rgba(165, 94, 234, 0.1)', borderRadius: '12px' }}>👾 GIF</span>
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('emoji'); }} style={{ color: '#ffa502', cursor: 'pointer', padding: '8px 12px', background: 'rgba(255, 165, 2, 0.1)', borderRadius: '12px' }}>🙂 Emoji</span>
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('location'); }} style={{ color: '#ff4757', cursor: 'pointer', padding: '8px 12px', background: 'rgba(255, 71, 87, 0.1)', borderRadius: '12px' }}>📍 Loc</span>
                <span onClick={(e) => { e.stopPropagation(); toggleMenu('feeling'); }} style={{ color: '#1e90ff', cursor: 'pointer', padding: '8px 12px', background: 'rgba(30, 144, 255, 0.1)', borderRadius: '12px' }}>🎭 Feeling</span>
                
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    {groupContext ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#333', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '8px 12px', fontSize: '13px', maxHeight: '110px', overflowY: 'auto' }}>
                            <span style={{ color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Post to:</span>
                            {userGroups.length === 0 && <span style={{ color: '#888' }}>No communities joined</span>}
                            {userGroups.map(g => (
                                <label key={g.slug} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'white' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedGroups.includes(g.slug)}
                                        onChange={() => toggleGroupSelection(g.slug)}
                                    />
                                    {g.name}
                                </label>
                            ))}
                        </div>
                    ) : (
                        <select value={postVisibility} onChange={(e) => setPostVisibility(e.target.value)} style={{ background: '#333', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '8px', borderRadius: '12px', cursor: 'pointer', outline: 'none' }}>
                            <option value="public" style={{background: '#333', color: 'white'}}>🌍 Public</option>
                            <option value="friends" style={{background: '#333', color: 'white'}}>👥 Friends</option>
                            <option value="private" style={{background: '#333', color: 'white'}}>🔒 Only Me</option>
                        </select>
                    )}

                    <button onClick={submitPost} style={{ background: '#2ed573', border: 'none', padding: '10px 30px', borderRadius: '20px', color: 'white', fontWeight: 'bold', cursor: 'pointer' }} disabled={isUploading || (videoState && (videoState.status === 'uploading' || videoState.status === 'processing')) || (groupContext && selectedGroups.length === 0)}>
                        {isUploading || (videoState && (videoState.status === 'uploading' || videoState.status === 'processing')) ? 'Wait' : 'POST'}
                    </button>
                </div>
          
                {activeMenu && (
                    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: '100%', left: 0, marginTop: '10px', zIndex: 10001, background: 'rgba(30, 30, 36, 0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '10px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)', width: '300px', maxWidth: '80vw' }}>
                        {activeMenu === 'emoji' && (<div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '5px', maxHeight: '200px', overflowY: 'auto' }}>{COMMON_EMOJIS.map(e => <div key={e} onClick={() => addEmoji(e)} style={{ fontSize: '24px', cursor: 'pointer', textAlign: 'center', padding: '5px' }}>{e}</div>)}</div>)}
                        {activeMenu === 'location' && (<div><div onClick={useMyLocation} style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.1)', color: '#ff4757', fontWeight: 'bold' }}>{geoLocating ? '📍 Locating...' : '📍 Use My Current Location'}</div><input style={{ width: '100%', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '8px', borderRadius: '6px', marginBottom: '5px', marginTop: '5px' }} placeholder="Search..." value={location} onChange={(e) => setLocation(e.target.value)} autoFocus />{VALID_LOCATIONS.filter(l => l.name.toLowerCase().includes(location.toLowerCase())).map(l => (<div key={l.name} onClick={() => { setLocation(l.name); setActiveMenu(null); }} style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{l.name}</div>))}</div>)}
                        {activeMenu === 'feeling' && (<div>{feelingsList.map(f => <div key={f} onClick={() => { setFeeling(f); setActiveMenu(null); }} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{f}</div>)}</div>)}
                        {activeMenu === 'ai' && (<div><div style={{ fontSize: '12px', color: '#aaa', marginBottom: '5px' }}>REWRITE STYLE:</div>{AI_TONES.map(t => <div key={t.value} onClick={() => handleMagicRewrite(t)} style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>{t.label}</div>)}</div>)}
                        {activeMenu === 'gif' && (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '300px' }}>
                                <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                                    <input value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search..." style={{ flex:1, padding:'8px', borderRadius:'6px', border:'none', background:'rgba(255,255,255,0.1)', color:'white' }} />
                                    <button onClick={() => searchGifs(gifSearch)} style={{ background:'#a55eea', border:'none', color:'white', borderRadius:'6px', cursor:'pointer' }}>🔍</button>
                                </div>
                                <div style={{ flex:1, overflowY:'auto', display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'5px' }}>
                                    {isGifLoading ? <div style={{ textAlign:'center', padding:'20px' }}>Loading...</div> : gifs.map(gif => (<img key={gif.id} src={gif.images.fixed_height.url} onClick={() => handleGifSelect(gif.images.original.url)} style={{ width:'100%', borderRadius:'4px', cursor:'pointer' }} alt={gif.title} />))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
          </div>

          {showQuickPost && createPortal(
              // Rendered via createPortal straight into <body> - this composer
              // can itself be nested inside another modal (the "Create Post"
              // overlay) whose ancestor .card has backdrop-filter set, which
              // creates a new containing block for position:fixed descendants
              // in Chrome. That silently repositioned this modal relative to
              // that smaller ancestor box instead of the real viewport,
              // pushing its top off-screen (confirmed live: cardRect.top was
              // -103px). Portaling to document.body sidesteps that class of
              // bug entirely, matching Post.js/Messages.js's existing
              // lightbox pattern. Header/footer are also now pinned outside
              // the scrollable question list so the close button and Generate
              // button are always reachable regardless of scroll position.
              <div onClick={() => setShowQuickPost(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                  <div onClick={(e) => e.stopPropagation()} style={{ width: '420px', maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: 'rgba(23, 23, 29, 0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', borderRadius: '20px', overflow: 'hidden' }}>
                      <div style={{ flexShrink: 0, padding: '25px 25px 15px 25px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                              <h3 style={{ margin: 0 }}>🪄 Quick Post</h3>
                              <span onClick={() => setShowQuickPost(false)} style={{ cursor: 'pointer', fontSize: '20px', color: '#aaa' }}>✕</span>
                          </div>
                          <p style={{ color: '#aaa', fontSize: '0.85rem', marginTop: '5px', marginBottom: 0 }}>Answer a couple quick questions and AI will draft a post - you'll get to review and edit it before it's posted.</p>
                      </div>
                      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 25px' }}>
                          {quickPostQuestions.map(q => (
                              <div key={q.question} style={{ marginBottom: '18px' }}>
                                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: '#ccc' }}>{q.question}</label>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {q.options.map(opt => (
                                          <div
                                              key={opt}
                                              onClick={() => selectQuickPostOption(q.question, opt)}
                                              style={{
                                                  padding: '8px 14px', borderRadius: '16px', cursor: 'pointer', fontSize: '0.85rem',
                                                  background: quickPostSelections[q.question] === opt ? '#a55eea' : 'rgba(255,255,255,0.08)',
                                                  border: quickPostSelections[q.question] === opt ? '1px solid #a55eea' : '1px solid rgba(255,255,255,0.1)',
                                                  color: 'white'
                                              }}
                                          >
                                              {opt}
                                          </div>
                                      ))}
                                      <div
                                          onClick={() => selectQuickPostOption(q.question, QUICK_POST_OTHER)}
                                          style={{
                                              padding: '8px 14px', borderRadius: '16px', cursor: 'pointer', fontSize: '0.85rem',
                                              background: quickPostSelections[q.question] === QUICK_POST_OTHER ? '#a55eea' : 'rgba(255,255,255,0.08)',
                                              border: quickPostSelections[q.question] === QUICK_POST_OTHER ? '1px solid #a55eea' : '1px solid rgba(255,255,255,0.1)',
                                              color: 'white'
                                          }}
                                      >
                                          Other...
                                      </div>
                                  </div>
                                  {quickPostSelections[q.question] === QUICK_POST_OTHER && (
                                      <input
                                          value={quickPostOtherText[q.question] || ''}
                                          onChange={(e) => setQuickPostOtherText(prev => ({ ...prev, [q.question]: e.target.value }))}
                                          placeholder="Type your own answer..."
                                          autoFocus
                                          style={{ width: '100%', marginTop: '8px', padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.25)', color: 'white', fontSize: '15px' }}
                                      />
                                  )}
                              </div>
                          ))}
                      </div>
                      <div style={{ flexShrink: 0, padding: '15px 25px 25px 25px' }}>
                          <button
                              onClick={handleQuickPostGenerate}
                              disabled={quickPostGenerating || Object.keys(quickPostSelections).length === 0}
                              style={{ width: '100%', background: '#a55eea', border: 'none', padding: '12px', borderRadius: '20px', color: 'white', fontWeight: 'bold', cursor: 'pointer', opacity: quickPostGenerating ? 0.7 : 1 }}
                          >
                              {quickPostGenerating ? 'Writing your post...' : 'Generate Post'}
                          </button>
                      </div>
                  </div>
              </div>,
              document.body
          )}
      </div>
  );

  return (
    <div className="feed-container">
      {/* TIMELINE MODAL - toggled by App.js's header calendar icon via showTimeline/onCloseTimeline props */}
      {showTimeline && (
          <div onClick={onCloseTimeline} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '40px', paddingLeft: '10px', paddingRight: '10px' }}>
              <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: '450px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', background: 'rgba(23, 23, 29, 0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', padding: '25px', borderRadius: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h3 style={{ margin: 0 }}>📅 Timeline</h3>
                      <button onClick={onCloseTimeline} style={{ background: 'none', border: 'none', color: '#ff4757', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer' }}>✕</button>
                  </div>
                  {archiveIndexLoading ? (
                      <div style={{ color: '#aaa', textAlign: 'center', padding: '10px' }}>Loading...</div>
                  ) : Object.keys(archiveIndex).length === 0 ? (
                      <div style={{ color: '#aaa', textAlign: 'center', padding: '10px' }}>No history yet.</div>
                  ) : (
                      Object.keys(archiveIndex).sort((a, b) => b - a).map(year => (
                          <div key={year}>
                              <div onClick={() => setExpandedYear(expandedYear === year ? null : year)} style={{ cursor: 'pointer', padding: '8px 0', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                  {year} <span>{expandedYear === year ? '▲' : '▼'}</span>
                              </div>
                              {expandedYear === year && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px 0' }}>
                                      {archiveIndex[year].map(month => (
                                          <div key={month} onClick={() => handleJumpToDate(year, month)} style={{ padding: '6px 14px', cursor: 'pointer', color: '#a0a0b0', background: 'rgba(255,255,255,0.05)', borderRadius: '15px' }}>
                                              {MONTH_NAMES[month - 1]}
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </div>
                  ))
              )}
              </div>
          </div>
      )}

      {archiveMode && (
          <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', marginBottom: '20px' }}>
              <span>📅 Viewing: {MONTH_NAMES[archiveMode.month - 1]} {archiveMode.year}</span>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {archiveLoading && <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Loading...</span>}
                  <button onClick={handleExitArchive} style={{ background: '#ff4757', border: 'none', color: 'white', padding: '6px 14px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.85rem' }}>Back to Today</button>
              </div>
          </div>
      )}

      {/* TRIGGER BAR - SAFE FOR GUESTS */}
      {showInput && !archiveMode && (
        <div className="card" onClick={() => !user ? onTriggerLogin() : setShowPostModal(true)} style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', cursor: 'pointer' }}>
            {user && user.profile_pic ? <img src={user.profile_pic} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} alt="Avatar" /> : <div style={{ background: user ? user.avatarColor : '#555', width: '40px', height: '40px', borderRadius: '50%' }}></div>}
            <div style={{ flex: 1, color: '#aaa', background: 'rgba(255,255,255,0.05)', padding: '12px 20px', borderRadius: '30px', border: '1px solid rgba(255,255,255,0.05)' }}>
                {user ? "What's on your mind?" : "Login to post..."}
            </div>
            <div style={{ fontSize: '20px', opacity: 0.5 }}>📷</div>
        </div>
      )}

      {/* MODAL - ONLY SHOW IF USER EXISTS */}
      {showPostModal && user && (
          <div onClick={() => { setShowPostModal(false); setActiveMenu(null); }} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '40px', paddingLeft: '10px', paddingRight: '10px' }}>
              <div className="card" onClick={(e) => { e.stopPropagation(); closeMenus(); }} style={{ width: '600px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'visible', background: 'rgba(23, 23, 29, 0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', padding: '25px', borderRadius: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                      <h3 style={{ margin: 0 }}>{groupContext ? 'Create Post in Community' : 'Create Post'}</h3>
                      <button onClick={() => setShowPostModal(false)} style={{ background: 'none', border: 'none', color: '#ff4757', fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer' }}>✕</button>
                  </div>
                  {renderEditor()}
              </div>
          </div>
      )}

      {/* BUG FIX: group-visibility posts were duplicating into the main
          feed for any member of that group. The backend deliberately still
          includes them in GET /api/posts for members (this exact posts
          array, passed down from App.js, is what GroupDetails.js filters
          client-side to get one group's posts) - excluding them here only
          when NOT inside a group's own page (groupContext falsy) was
          always this component's job, which it never did. When
          groupContext IS set, the posts prop is already GroupDetails.js's
          own pre-filtered subset for that one group, so nothing here
          should be excluded in that case. */}
      {(archiveMode ? archivePosts : posts).filter((p) => groupContext || p.visibility !== 'group').map((postData, postIndex) => (
        <React.Fragment key={postData.id}>
          <div style={{ borderRadius: '20px', marginTop: '10px', transition: 'box-shadow 0.3s ease', overflow: 'hidden', ...getGlowStyle(postData.sentiment) }}>
              <Post
                  post={postData} 
                  user={user} 
                  onDelete={onDeletePost} 
                  onLike={onLikePost} 
                  onAddComment={onAddComment}
                  onVoteComment={onVoteComment}
                  onDeleteComment={onDeleteComment}
                  onEditComment={onEditComment} 
                  onEdit={onEdit} 
                  onNavigateProfile={onNavigateProfile} 
                  onShare={handleShare}
                  onTriggerLogin={onTriggerLogin} // PASS DOWN
                  giphyKey={giphyKey}
              />
          </div>
          {/* Native ad every 7 posts, matching the mobile AdMob feed interval - see AdPost.js */}
          {!archiveMode && !groupContext && postIndex > 0 && (postIndex + 1) % 7 === 0 && (
              <div style={{ borderRadius: '20px', marginTop: '10px', overflow: 'hidden' }}>
                  <AdPost />
              </div>
          )}
        </React.Fragment>
      ))}
      {!archiveMode && hasMore && (
          <div ref={loadMoreSentinelRef} style={{ height: '1px' }} />
      )}
    </div>
  );
}

export default Feed;
